<template>
  <div v-if="filterProperties" :class="['facet-filter', { open } ]">
    <button class="button--close" @click="hideOnMobile">{{ translate('btnFilterHide') }}<close></close></button>
    <div class="inner">
      <button class="button--reset" @click="resetFilter" v-text="translate('btnFilterReset')"></button>
      <h3 v-text="translate('lblFilterHeadline')"></h3>

      <toggle
        v-for="property in toggles" :key="property.id"
        :label="property.name"
        :id="slugify(property.id)"
        :collapsed="false"
      >
        <ul>
          <li v-for="entry in property.entries" :key="`${crop}-${property.id || property.name}-${entry.id}-${entry.checked}`">
            <input
              type="checkbox"
              :id="slugify(`${crop}-${property.id || property.name}-${entry.id}`)"
              v-model="entry.checked"
              :disabled="checkboxDisabled(entry)"
              :class="{ mandatory: entry.mandatory && (entry.numProducts > 0 || entry.numProducts === null) }"
              @change="check(property.id, entry.id, entry)"
            />
            <label :for="slugify(`${crop}-${property.id || property.name}-${entry.id}`)">
              <span>{{ entry.name }}</span>
              <span class="quantity-results" v-text="entry.numProducts" v-if="`${entry.numProducts}`"></span>
              <span :class="{'icon-tick': !entry.loading}">
                <icon-spinner v-if="entry.loading" />
                <icon-tick v-else />
              </span>
            </label>
          </li>
        </ul>
      </toggle>
    </div>
  </div>
</template>

<script>
import { slugify } from '../../shared/Utils';
import Close from '../../shared/components/icons/Close.vue';
import Toggle from '../../shared/components/content-toggle.vue';
import IconTick from '../../shared/components/icons/Tick.vue';
import IconSpinner from '../../shared/components/icons/Spinner.vue';

export default {
  name: 'FacetFilter',
  emits: [
    'changed',
    'reset-sort-order',
    'close',
  ],
  props: {
    filterProperties: {
      type: Object,
      required: true,
    },
    open: {
      type: Boolean,
      default: false,
    },
    crop: String,
    defaultSettings: Object,
    currentSettings: Object,
    filterSettingsKey: String,
  },
  computed: {
    toggles() {
      let toggles = [];
      if (this.filterProperties?.properties) {
        toggles = toggles.concat(...this.filterProperties.properties);
      }
      if (this.filterProperties?.usages) {
        toggles.unshift(this.filterProperties.usages);
      }
      return toggles;
    },

  },
  watch: {
    filterProperties: {
      handler() {
        this.setFilterTo(this.currentSettings);
      },
    },
  },
  methods: {
    slugify(string) {
      return slugify(string);
    },
    hideOnMobile() {
      this.$emit('close');
    },
    resetFilter() {
      this.emptyFilter();
      if (this.defaultSettings) {
        this.setFilterTo(this.defaultSettings);
      }
      this.$forceUpdate();
      this.emitChange();
    },
    emptyFilter() {
      this.filterProperties.properties.forEach((p) => {
        p.entries.forEach((e) => {
          e.checked = false;
        });
      });
      // eslint-disable-next-line no-unused-expressions
      this.filterProperties.usages?.entries?.forEach((e) => {
        e.checked = false;
      });

      this.$emit('reset-sort-order');
    },
    setFilterTo(settings) {
      if (settings?.properties?.length) {
        settings.properties.forEach((defaultSetting) => {
          if (this.filterProperties?.properties) {
            this.filterProperties
              .properties.find((p) => p.id === defaultSetting.propertyId)
              .entries
              .find((e) => e.id === defaultSetting.entryId)
              .checked = true;
          }
        });
      }
      if (settings?.usageId && this.filterProperties?.usages) {
        this.filterProperties.usages.entries.forEach((e) => {
          e.checked = e.id === settings.usageId;
        });
      }

      // Ensure, at least one usage is chosen
      if (
        this.filterProperties?.usages?.entries?.length
        && !this.filterProperties?.usages?.entries.some((e) => e.checked)
      ) {
        // todo: actually you should not modify properties to avoid side effects of your components
        // eslint-disable-next-line
        this.filterProperties.usages.entries[0].checked = true;
      }
    },
    check(propertyId, entryId, entry) {
      // eslint-disable-next-line no-param-reassign
      entry.loading = true;
      if (!this.isPropertyMultiselect(propertyId)) {
        this.ensureUnique(propertyId, entryId);
      }
      if (entry.checked) {
        this.trackFilter(entryId);
      }
      this.emitChange();
    },
    isPropertyMultiselect(propertyId) {
      return this.getProperty(propertyId).multipleSelect;
    },
    ensureUnique(propertyId, entryId) {
      const property = this.getProperty(propertyId);
      property.entries.forEach((e) => {
        if (e.id !== entryId) {
          // eslint-disable-next-line no-param-reassign
          e.checked = false;
        }
      });
      this.$forceUpdate();
    },
    emitChange() {
      const propertyIds = this.filterProperties.properties?.map(
        (p) => (
          {
            propertyId: p.id,
            entryId: p.entries.filter(
              (u) => u.checked,
            ).map(
              (u) => u.id,
            ),
          }
        ),
      ).filter(
        (p) => p.entryId,
      );
      const propertyPayload = [];
      propertyIds.forEach(
        (property) => property.entryId?.forEach(
          (entryId) => propertyPayload.push(
            {
              propertyId: property.propertyId,
              entryId,
            },
          ),
        ),
      );
      const userInput = {
        usageId: this.filterProperties.usages?.entries.filter(
          (u) => u.checked,
        ).map(
          (u) => u.id,
        ).pop(),
        properties: propertyPayload,
      };
      this.$emit('change', userInput);
    },
    checkboxDisabled(entry) {
      const productsAvailable = entry.numProducts === null || entry.numProducts > 0;
      const mandatoryAndChecked = entry.mandatory && entry.checked;
      return !productsAvailable || mandatoryAndChecked;
    },
    getProperty(propertyId) {
      let found = this.filterProperties.properties.find((p) => p.id === propertyId);
      if (!found) {
        // usages are mandatory. other filterProperties may change and are stores in the properties array.
        found = this.filterProperties.usages;
      }
      return found;
    },
    trackFilter(filterId) {
      const trackingObject = {
        tracking: {
          action: 'value_change',
          business_unit: this.crop,
          category: 'tools',
          tool: 'variety advisor',
          value: filterId,
        },
        event: 'farmers_cockpit_value_change',
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(trackingObject);
    },
  },
  created() {
    this.setFilterTo(this.currentSettings);
  },
  components: {
    Toggle,
    Close,
    IconTick,
    IconSpinner,
  },
};
</script>

<style lang="scss">
.facet-filter .inner {
  background-color: $color-kws-black-5;

  .bg-white & {
    background-color: $color-white;
  }
}
</style>

<style lang="scss" scoped>
$inner-padding: rem(10px);

.facet-filter {
  display: none;

  &.open {
    display: flex;
  }
  z-index: 20;
  background-color: rgba(0,0,0,.75);
  margin-bottom: rem(16px);
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  flex-direction: column;

  .inner {
    padding: 0 $inner-padding 0;
    overflow: scroll;
  }

  .button--close {
    display: block;
    background-color: $color-white;
    color: $color-kws-black-90;
    border: none;
    border: 0;
    width: 100%;
    margin: 0;
    vertical-align: middle;
    cursor: pointer;
    padding: rem(16px) #{$inner-padding};
    text-align: start;
    position: relative;

    svg {
      top: rem(14px);
      inset-inline-end: rem(20px);
      height: rem(20px);
      z-index: 1;
      position: absolute;
    }
  }
  .button--reset {
    background-color: transparent;
    display: block;
    width: 100%;
    text-align: start;
    padding: rem(16px) 0;
    color: #ed6d00;
    border: none;
    border: 0;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
  }

  h3 {
    font-weight: 700;
  }

  @include media-breakpoint-up(m) {
    display: block;
    background-color: transparent;
    position: unset;

    .button--close {
      display: none;
    }

    .inner {
      overflow: unset;
    }
  }

  ul {
    padding: 0 0 rem(24px) 0;
    margin: 0;
    list-style: none;

    li {
      padding-bottom: rem(10px);
      &:last-child {
        padding-bottom: 0;
      }
      input[type='checkbox'] {
        appearance: none;
        opacity: 0;
        position: absolute;

        &:checked + label span {

          &.icon-tick {
            svg {
              transform: translateY(2px);
            }
          }
          &:nth-child(3) {
            background-color: $color-kws-orange;
            color: $color-white;

            svg {
              display: block;
            }
          }
        }

        &:disabled {
          & + label {
            cursor: default;
          }
          &:not(.mandatory) + label span:nth-child(3) {
            // class mandatory is added, if number of products is > 0 and at least one checkbox has
            // to be checked. in this case an already checked box can not be unchecked but because
            // of there are products the border should not appear grey
            border-color: $color-kws-light-gray-100;
          }
        }

      }
      label {
        display: flex;
        gap: rem(12px);
        span:first-child {
          flex-grow: 1;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        span.quantity-results {
          flex-shrink: 0;
          font-weight: bold;
        }
        span:nth-child(3) {
          box-sizing: content-box;
          flex-shrink: 0;
          margin-top: rem(1px);
          height: rem(14px);
          width: rem(14px);
          background-color: $color-white;
          border: 2px solid $color-kws-orange;
          color: $color-black;

          svg {
            width: rem(14px);
            height: rem(14px);
            fill: $color-white;
            vertical-align: top;
          }

          &.icon-tick svg {
            display: none;
          }
        }
      }
    }
  }

  ::v-deep .content-toggle button {
    margin: 0 -#{$inner-padding};
    padding: rem(6px) $inner-padding;
    background-color: $color-kws-light-gray-75;
    width: calc(100% + 2 * #{$inner-padding});
    text-align: start;
    border: none;
    border: 0;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
  }

  .rotate {
    animation: rotate 1.5s linear infinite;
  }

  @keyframes rotate{
    to{ transform: rotate(360deg); }
  }
}
</style>
